import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Runs `onReady` (which must be stable, e.g. a useCallback) once the user is
 * signed in with an advertiser resolved, and again whenever that context
 * changes. Shared by the pending-prompt hooks (useShopifyPendingConfirm,
 * useAmazonPendingAuthorize), which surface a persisted entry only then:
 *
 *   - waits for a ?advertiser_id= masquerade deep link to be applied and
 *     stripped first, so it never decides on a support user's own
 *     not-yet-masqueraded state;
 *   - a support user with no active masquerade gets nothing, and the caller's
 *     entry is kept (they may masquerade as the target advertiser next);
 *   - waits for advertisersLoaded and a resolved effectiveAdvertiserId that is
 *     one of the user's own advertisers (or the masqueraded one).
 */
export function useResolvedAdvertiserGate(onReady) {
  const {
    session,
    loading,
    effectiveAdvertiserId,
    isSupportUser,
    isMasquerading,
    advertisers,
    advertisersLoaded,
  } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (loading || !session) return;
    if (searchParams.has("advertiser_id")) return;
    if (isSupportUser && !isMasquerading) return;
    if (!advertisersLoaded) return;
    if (effectiveAdvertiserId == null) return;
    if (!isMasquerading && !advertisers.some((a) => a.id === effectiveAdvertiserId)) return;
    onReady();
  }, [
    loading,
    session,
    effectiveAdvertiserId,
    isSupportUser,
    isMasquerading,
    advertisers,
    advertisersLoaded,
    searchParams,
    onReady,
  ]);
}
